/**
 * Public Scene Gallery Component
 *
 * Displays a public gallery of user-created scenes.
 * Shows off community creations and allows viewing/sharing.
 */

import React, { useState, useEffect } from 'react';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import StaticSceneDisplay from './StaticSceneDisplay';
import navigationService from '../services/navigationService';
import parallaxService from '../services/parallaxService';
import logger from '../utils/logger';
import './PublicSceneGallery.css';

const PublicSceneGallery = () => {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'popular', 'name'
  const [searchTerm, setSearchTerm] = useState('');

  // Load public scenes
  useEffect(() => {
    const loadScenes = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all public scenes
        const allScenes = await parallaxService.getAllScenes();

        // Filter for public scenes with valid sticker data
        const publicScenes = (Array.isArray(allScenes) ? allScenes : []).filter((scene) => {
          // Filter out empty or invalid scenes
          if (!scene.name || !scene.scene_stickers || scene.scene_stickers.length === 0) {
            return false;
          }

          // Filter out test/dev scenes (case-insensitive)
          const testKeywords = ['test', 'broken', 'debug', 'not saving', 'cursor', 'speed'];
          const nameLower = scene.name.toLowerCase();
          if (testKeywords.some((keyword) => nameLower.includes(keyword))) {
            return false;
          }

          // Filter out scenes with very generic/placeholder names
          if (nameLower.match(/^\d+$/)) {
            // Just numbers
            return false;
          }

          // TODO: Add isPublic filter when backend supports it
          // return scene.isPublic === true;
          return true;
        });

        setScenes(publicScenes);
      } catch (err) {
        console.error('❌ Error loading scenes:', err);
        setError('Unable to load scene gallery. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadScenes();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get sticker count from scene
  const getStickerCount = (scene) => {
    if (!scene.scene_stickers) return 0;
    return Array.isArray(scene.scene_stickers) ? scene.scene_stickers.length : 0;
  };

  // Sort scenes
  const sortedScenes = React.useMemo(() => {
    let sorted = [...scenes];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      sorted = sorted.filter(
        (scene) =>
          scene.name?.toLowerCase().includes(term) ||
          scene.creatorName?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        sorted.sort((a, b) => {
          const dateA = new Date(b.updatedAt || b.createdAt || 0);
          const dateB = new Date(a.updatedAt || a.createdAt || 0);
          return dateA - dateB;
        });
        break;
      case 'popular':
        sorted.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
        break;
      case 'name':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    return sorted;
  }, [scenes, sortBy, searchTerm]);

  // View scene in full viewer
  const handleViewScene = (scene) => {
    logger.log('🎨 Viewing scene:', scene.name);
    // Navigate to scene viewer with scene name
    navigationService.navigateTo('scene-viewer');
    // Store scene name for viewer to load
    sessionStorage.setItem('viewingSceneName', scene.name);
  };

  // Loading state
  if (loading) {
    return (
      <>
        <MainHeader currentPage="gallery" />
        <div className="public-gallery loading">
          <div className="loading-message">Loading scene gallery...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <MainHeader currentPage="gallery" />
      <div className="public-gallery">
        <div className="gallery-container">
          {/* Page Header */}
          <div className="gallery-header">
            <div className="gallery-header-content">
              <h1>Scene Gallery</h1>
              <p className="gallery-subtitle">Explore amazing scenes created by our community</p>
            </div>
            <button
              className="create-scene-btn"
              onClick={() => navigationService.navigateTo('scene-builder')}
            >
              <MaterialIcon icon="add" size={20} />
              Create Your Own
            </button>
          </div>

          {/* Controls */}
          <div className="gallery-controls">
            {/* Search */}
            <div className="gallery-search">
              <MaterialIcon icon="search" size={20} />
              <input
                type="text"
                placeholder="Search scenes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <MaterialIcon icon="close" size={18} />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="gallery-sort">
              <MaterialIcon icon="sort" size={20} />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>

            {/* Scene Count */}
            <div className="gallery-count">
              {sortedScenes.length} scene{sortedScenes.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="gallery-error">
              <MaterialIcon icon="error" size={32} />
              <p>{error}</p>
              <button className="retry-btn" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!error && sortedScenes.length === 0 && !searchTerm && (
            <div className="gallery-empty">
              <MaterialIcon icon="palette" size={80} />
              <h2>No Public Scenes Yet</h2>
              <p>
                Be the first to create and share a scene! Build something amazing in the Scene
                Builder.
              </p>
              <button
                className="create-first-scene-btn"
                onClick={() => navigationService.navigateTo('scene-builder')}
              >
                <MaterialIcon icon="add_circle" size={20} />
                Create First Scene
              </button>
            </div>
          )}

          {/* No Search Results */}
          {!error && sortedScenes.length === 0 && searchTerm && (
            <div className="gallery-empty">
              <MaterialIcon icon="search_off" size={80} />
              <h2>No Results Found</h2>
              <p>No scenes match "{searchTerm}". Try a different search term.</p>
              <button className="clear-search-btn-large" onClick={() => setSearchTerm('')}>
                Clear Search
              </button>
            </div>
          )}

          {/* Scenes Grid */}
          {!error && sortedScenes.length > 0 && (
            <div className="gallery-grid">
              {sortedScenes.map((scene) => (
                <div
                  key={scene.name}
                  className="gallery-scene-card"
                  onClick={() => handleViewScene(scene)}
                >
                  {/* Scene Thumbnail/Preview */}
                  <div className="scene-preview">
                    {scene.scene_stickers && scene.scene_stickers.length > 0 ? (
                      <StaticSceneDisplay
                        sceneStickers={scene.scene_stickers}
                        height="240px"
                        backgroundColor="#1a1a1a"
                        scaleToFit={true}
                      />
                    ) : (
                      <div className="preview-placeholder">
                        <MaterialIcon icon="image" size={48} />
                        <span>No Preview</span>
                      </div>
                    )}
                    <div className="preview-overlay">
                      <MaterialIcon icon="visibility" size={32} />
                      <span>View Scene</span>
                    </div>
                  </div>

                  {/* Scene Info */}
                  <div className="scene-card-info">
                    <h3 className="scene-card-name">{scene.name}</h3>

                    {/* Metadata */}
                    <div className="scene-card-meta">
                      <div className="meta-item">
                        <MaterialIcon icon="layers" size={14} />
                        <span>{getStickerCount(scene)} stickers</span>
                      </div>
                      {scene.viewCount > 0 && (
                        <div className="meta-item">
                          <MaterialIcon icon="visibility" size={14} />
                          <span>{scene.viewCount} views</span>
                        </div>
                      )}
                    </div>

                    {/* Creator & Date */}
                    <div className="scene-card-footer">
                      {scene.creatorName && (
                        <div className="scene-creator">
                          <MaterialIcon icon="person" size={14} />
                          <span>{scene.creatorName}</span>
                        </div>
                      )}
                      <div className="scene-date">
                        {formatDate(scene.updatedAt || scene.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PublicSceneGallery;
